// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { AccountSummaryApiParameter } from "@steiner/App/Services/AccountSummary";
import { Draft } from "immer";

export interface AccountSummary {
    terms: string;
    creditLimit: string;
    availableCredit: string;
    total: string;
    lastPurchase: string;
    lastPayment: string;
    mtdPurchases: string;
    ytdPurchases: string;
    paymentDays: string;
    future: string;
    current: string;
    days31To60: string;
    days61To90: string;
    days91To120: string;
    daysOver120: string;
    years: number[];
    customerName: string;
}

export type AccountSummaryState = {
    accountSummary?: AccountSummary;
    getAccountSummaryParameter?: AccountSummaryApiParameter;
    isLoading?: boolean;
};

const initialState: AccountSummaryState = {
    isLoading: false,
};

const xAccountSummary = createTypedReducerWithImmer(initialState, {
    "xAccountSummary/BeginLoadingAccountSummary": (
        draft: Draft<AccountSummaryState>,
        action: { isLoading: boolean },
    ) => {
        draft.isLoading = action.isLoading;
    },
    "xAccountSummary/CompleteLoadingAccountSummary": (
        draft: Draft<AccountSummaryState>,
        action: { value: AccountSummary },
    ) => {
        draft.accountSummary = action.value;
        draft.isLoading = false;
    },
    "xAccountSummary/FailedLoadingAccountSummary": (draft: Draft<AccountSummaryState>) => {
        draft.isLoading = false;
    },
});

(reducers as any).xAccountSummary = xAccountSummary;

export type xAccountSummaryState = {
    xAccountSummary: AccountSummaryState;
} & ApplicationState;

type AccountSummaryReducer = {
    accountSummaryData: typeof xAccountSummary;
};

export type AccountSummaryDataActions = Parameters<AccountSummaryReducer[keyof AccountSummaryReducer]>[1] | AnyAction;
